import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { Button, ChartLegend } from 'common/components';
import { CustomLabel } from './components/CustomLabel';
import {
  shortenFormat,
  removeWhiteSpaceAndNewLineAndLowercase,
} from 'common/formatters';
import {
  Bar,
  VictoryBar,
  VictoryContainer,
  VictoryAxis,
  VictoryStack,
} from 'victory';
import { withResizeDetector } from 'react-resize-detector';
import IEVictoryChart from 'common/IEVictoryChart';
import { COLOR_SCALE } from 'common/constants';
import { PinstripePattern } from 'common/svg/PinstripePattern';
import './index.css';

class OverhangingBar extends React.Component {
  static propTypes = {
    cornerRadius: Bar.propTypes.cornerRadius,
    scale: Bar.propTypes.scale,
    y0: Bar.propTypes.y0,
  };

  render() {
    const { cornerRadius, scale, y0, ...otherProps } = this.props;
    return (
      <Bar
        y0={Math.min(scale.y(0), y0 + cornerRadius)}
        cornerRadius={cornerRadius}
        scale={scale}
        {...otherProps}
      />
    );
  }
}

/**
 *
 *
 * @export
 * @class BarChart
 * @extends {PureComponent}
 */
export class BarChart extends PureComponent {
  static displayName = 'BarChart';

  static propTypes = {
    browser: PropTypes.object,
    width: PropTypes.number,
    height: PropTypes.number,
    colorScale: PropTypes.arrayOf(PropTypes.string),
    data: PropTypes.shape({
      getAllCropsForField: PropTypes.array,
      getRevenueForecast: PropTypes.shape({
        contracted: PropTypes.arrayOf(
          PropTypes.shape({ label: PropTypes.string, value: PropTypes.number })
        ),
        surplus: PropTypes.arrayOf(
          PropTypes.shape({ label: PropTypes.string, value: PropTypes.number })
        ),
        deficit: PropTypes.arrayOf(
          PropTypes.shape({ label: PropTypes.string, value: PropTypes.number })
        ),
      }),
      rawRevenueData: PropTypes.object,
    }),
    toggleChart: PropTypes.func,
  };

  static defaultProps = {
    colorScale: COLOR_SCALE,
    data: { getRevenueForecast: {}, getAllCropsForField: [] },
  };

  state = {
    toggleLegendTooltip: false,
  };

  componentDidMount() {
    document.addEventListener('mousedown', this.handleClickOutside);
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside);
  }

  render() {
    return (
      <div data-testid="BarChart" className="barchart">
        <div className="barchart__legend">
          {this.getLegendTooltipButton()}
          {this.getLegendTooltip()}
          <ChartLegend
            pattern={[null, <PinstripePattern key="secretKey" />, null]}
            data={['Contracted', 'Contract Deficit', 'Uncontracted']}
            colorScale={['#006834', '#008543', '#9bbb00']}
          />
        </div>

        <PinstripePattern />
        {this.getChart()}
      </div>
    );
  }

  /**
   *
   *
   * Returns the price buttons for the crops without price input
   * @function renderPriceButtons
   * @memberof BarChart
   * @returns {React.Dom}
   * @public
   */
  renderPriceButtons = (data, responsiveWidth) => {
    // do not render anything if there's no data
    if (!data.length) {
      return null;
    }

    // prices is from `getAllCropsForField`
    const {
      data: { getAllCropsForField },
      toggleChart,
    } = this.props;

    const fieldCrops = getAllCropsForField;

    const renderPriceButton = (item, idx) => {
      // if there is no matching crop in prices (field crops), could be contact crops
      const isFieldCrop =
        fieldCrops &&
        fieldCrops.find(fieldCrop => {
          const { crop } = fieldCrop;
          const fieldCropName = removeWhiteSpaceAndNewLineAndLowercase(
            `${crop.name}${crop.variety ? crop.variety : ''}`
          );
          const itemCropName = removeWhiteSpaceAndNewLineAndLowercase(
            item.name
          );
          return fieldCropName === itemCropName;
        });
      // if this is field crop, find out if price value is added
      const hasPrice =
        isFieldCrop &&
        isFieldCrop.expectedPrice &&
        isFieldCrop.expectedPrice.value;

      return (
        <li key={`${item.label}${idx}`}>
          {isFieldCrop && !hasPrice && (
            <Button onClick={toggleChart} className="btn btn--info">
              Missing Price
            </Button>
          )}
        </li>
      );
    };

    const chartCSS = cn(
      'barchart__belowaxis',
      data.length > 4 && 'barchart__belowaxis--crowded'
    );
    return (
      data.length && (
        <div style={{ width: `${responsiveWidth}px` }}>
          <ul className={chartCSS}>{data.map(renderPriceButton)}</ul>
        </div>
      )
    );
  };

  /**
   *
   *
   * Returns the chart in a scrollable container if on a mobile device
   * @function getChart
   * @memberof BarChart
   * @returns {React.Dom}
   * @public
   */
  getChart = () => {
    const { browser } = this.props;
    const { contracted, surplus, deficit } = this.props.data.getRevenueForecast;
    const filteredContracted = this.convertNullToZero(
      this.splitLabels(contracted)
    );
    const filteredSurplus = this.convertNullToZero(this.splitLabels(surplus));
    const filteredDeficit = this.convertNullToZero(this.splitLabels(deficit));

    const sortedRevenueForcast = {
      contracted: filteredContracted,
      surplus: filteredSurplus,
      deficit: filteredDeficit,
    };

    const extrabars = surplus && surplus.length > 6 ? surplus.length - 6 : 0;
    const responsiveWidth =
      (browser.greaterThan.mobile ? 950 : 750) + extrabars * 50;
    const responsiveHeight = browser.greaterThan.mobile ? 400 : 300;

    const barRatio = getBarRatio(surplus);
    const axisStyle = {
      fontSize: 14,
      padding: 5,
      fill: '#4F4F4F',
      fontFamily: '"Univers LT Roman", sans-serif',
    };

    return (
      <div className="scrollable">
        <IEVictoryChart
          domainPadding={getDomainPadding(contracted, surplus)}
          width={responsiveWidth}
          height={responsiveHeight}
          padding={{ top: 10, bottom: 40, left: 60, right: 40 }}
          containerComponent={<VictoryContainer responsive={false} />}
        >
          <VictoryAxis
            dependentAxis
            crossAxis={false}
            standalone={false}
            tickFormat={d =>
              !isNaN(d) && (d || d === 0) ? this.shortenThousand(d) : ''
            }
            style={{
              axis: { strokeOpacity: 0 },
              tickLabels: axisStyle,
              grid: { stroke: '#f6f6f6' },
            }}
          />
          <VictoryStack
            labelComponent={this.getCustomTooltip(
              responsiveWidth,
              responsiveHeight,
              browser.greaterThan.mobile ? 300 : 230,
              sortedRevenueForcast
            )}
          >
            <VictoryBar
              barRatio={barRatio}
              cornerRadius={barRatio + 5.75}
              x="label"
              y="value"
              data={filteredContracted}
              style={{ data: { fill: '#006834' } }}
              alignment="middle"
              events={this.getBarEvents()}
              dataComponent={<OverhangingBar />}
            />
            <VictoryBar
              barRatio={barRatio}
              cornerRadius={barRatio + 5.75}
              x="label"
              y="value"
              data={filteredSurplus}
              style={{ data: { fill: '#9bbb00' } }}
              alignment="middle"
              events={this.getBarEvents()}
              dataComponent={<OverhangingBar />}
            />
            <VictoryBar
              barRatio={barRatio}
              cornerRadius={barRatio + 5.75}
              x="label"
              y="value"
              data={filteredDeficit}
              style={{ data: { fill: 'url(#pinstripe)' } }}
              alignment="middle"
              events={this.getBarEvents()}
              dataComponent={<OverhangingBar />}
            />
          </VictoryStack>
          <VictoryAxis
            style={{
              axis: { strokeOpacity: 0 },
              tickLabels: axisStyle,
            }}
          />
        </IEVictoryChart>
        {this.renderPriceButtons(filteredSurplus, responsiveWidth)}
      </div>
    );
  };

  /**
   *
   * Handles the Barchart events
   * @function getBarEvents
   * @memberof BarChart
   * @returns {array}
   * @public
   */
  getBarEvents = () => {
    return [
      {
        target: 'data',
        eventHandlers: {
          onClick: () => {
            return [
              {
                target: 'labels',
                mutation: () => ({ active: true }),
              },
            ];
          },
          onMouseOut: () => {
            return [
              {
                target: 'labels',
                mutation: () => ({ active: false }),
              },
            ];
          },
        },
      },
    ];
  };

  /**
   *
   * Fetches the `<CustomLabel />` component
   * @function getCustomTooltip
   * @memberof BarChart
   * @param {Number} width
   * @param {Number} height
   * @param {Number} tooltipWidth
   * @returns {React.Dom}
   * @public
   */
  getCustomTooltip = (width, height, tooltipWidth, sortedRevenueForecast) => {
    const { contracted, surplus, deficit } = sortedRevenueForecast;

    return (
      <CustomLabel
        contracted={contracted}
        surplus={surplus}
        deficit={deficit}
        chartWidth={width}
        chartHeight={height}
        tooltipWidth={tooltipWidth}
        rawRevenueData={this.props.data.rawRevenueData}
      />
    );
  };

  /**
   *
   *
   * Converts all null values in an array to 0's
   * @function convertNullToZero
   * @memberof BarChart
   * @param {array}
   * @returns {array}
   * @public
   */
  convertNullToZero = array => {
    // undefined, null, 0 must be replaced to 0 here
    return (array || []).map(obj => ({
      ...obj,
      value: obj.value ? obj.value : 0,
    }));
  };

  /**
   *
   *
   * Splits strings into multi-line strings for the chart labels
   * @function splitLabels
   * @memberof BarChart
   * @param {array}
   * @returns {Array}
   * @public
   */
  splitLabels = array => {
    return array.map(({ label, value }) => {
      // split label by space
      const arr = label.trim().split(' ');
      // put the first word (expecting crop name) at first
      // put the rest (shorten by 10 letters) at second
      const newLabel = [arr[0], shortenFormat(arr.slice(1).join(' '), 10)];

      return {
        name: arr.join('\n'),
        label: newLabel.join('\n'),
        value: value === null ? 0 : value,
      };
    });
  };

  /**
   *
   * Toggles the legend tooltip based on state
   * @function toggleLegendTooltip
   * @memberof BarChart
   * @public
   */
  toggleLegendTooltip = () => {
    this.setState({ toggleLegendTooltip: !this.state.toggleLegendTooltip });
  };

  /**
   *
   * Renders the legend tooltip trigger button
   * @function getLegendTooltipButton
   * @memberof BarChart
   * @returns {React.Dom}
   * @public
   */
  getLegendTooltipButton = () => {
    return (
      <a
        className="barchart__key"
        onClick={this.toggleLegendTooltip}
        ref={node => (this.tooltipButton = node)}
      >
        KEY <span className="barchart__keyIcon">i</span>
      </a>
    );
  };

  /**
   *
   * Renders the legend tooltip
   * @function getLegendTooltip
   * @memberof BarChart
   * @returns {React.Dom}
   * @public
   */
  getLegendTooltip = () => {
    const tooltip = (
      <div
        className="barchart__tooltip"
        ref={node => (this.tooltipContainer = node)}
      >
        <h3 className="barchart__tooltip__title">How it’s calculated</h3>
        <dl className="barchart__tooltip__list">
          <dt>
            <i className="barchart__dot barchart__dot--primary" />
            Contracted
          </dt>
          <dd>
            Expected revenue from contracts (Contracted Qty multipled by Base
            Price)
          </dd>
        </dl>

        <dl className="barchart__tooltip__list">
          <dt>
            <i className="barchart__dot">
              <PinstripePattern />
            </i>
            Contract Deficit
          </dt>
          <dd>
            Lost revenue in case expected harvest quantity is less than the
            contracted quantity
          </dd>
        </dl>

        <dl className="barchart__tooltip__list">
          <dt>
            <i className="barchart__dot barchart__dot--secondary" />
            Uncontracted
          </dt>
          <dd>
            Expected revenue from surplus grain (Expected harvest Qty minus
            Contracted Qty multipled by Expected Price)
          </dd>
        </dl>
      </div>
    );
    return this.state.toggleLegendTooltip ? tooltip : null;
  };

  /**
   *
   * Handles clicking outside of the tooltip
   * @function handleClickOutside
   * @memberof BarChart
   * @param {object} e
   * @public
   */
  handleClickOutside = e => {
    if (
      this.tooltipContainer &&
      this.tooltipButton &&
      !this.tooltipContainer.contains(e.target) &&
      !this.tooltipButton.contains(e.target) &&
      this.state.toggleLegendTooltip
    ) {
      this.toggleLegendTooltip();
    }
  };

  /**
   *
   * Shortens thousand numbers like 100,000 into 100
   * @function shortenThousand
   * @memberof BarChart
   * @param {Number}
   * @returns {Number}
   * @public
   */
  shortenThousand = num => {
    const inputNumber = Number(num);
    return inputNumber > 999 ? `${+(inputNumber / 1000).toFixed(1)} k` : num;
  };
}

export default compose(
  withResizeDetector,
  connect(({ browser }) => ({ browser }))
)(BarChart);

/**
 *
 *
 * @function getBarRatio
 * @returns {number}
 * @param {array} - surplus
 * @public
 * This function returns a bar ratio of 6.5 if there is only one bar
 * else it returns 0.25
 */
export const getBarRatio = bars => {
  return Object.keys(bars).length === 1 ? 6.5 : 0.25;
};

/**
 *
 *
 * @function getDomainPadding
 * @param {array} contracted
 * @param {array} surplus
 * @returns {Array}
 * @public
 * This function calculates the padding beside each bar based on how many bars there are
 */
export const getDomainPadding = (contracted, surplus) => {
  return [20 + 100 / surplus.length, 20 + 100 / contracted.length];
};



// WEBPACK FOOTER //
// ./src/common/components/BarChart/index.js